import * as Icons from "@nova/component-icons"
import { SidePanelLayerType } from "@nova/constants"
import { UserLocationShare } from "@nova/protobuf"
import {
    selectLiveUserLocationShares,
    selectNonLiveUserLocationShares,
    selectUserLocationShareById,
    selectUserLocationShares,
} from "features/entity-caching/flights/selectors"
import { getUseSidePanelEntities } from "panels/content/side-panel/generics/genericUseSidePanelEntities"
import {
    deleteUserLocationShare,
    updateUserLocationShare,
} from "features/pubsub/entityActions/flights/userLocationShare"
import { useUserLocationLayers } from "features/map/layers/user-locations/UserLocationLayer"
import { useUserLocationSharePlaybackLayers } from "features/map/layers/user-locations/UserLocationSharePlaybackLayer"
import { useUserLocationPathLayer } from "features/map/layers/user-locations/UserLocationPathLayer"
import { userLocationSharePlaybackStore } from "features/map/layers/user-locations/userLocationSharePlaybackStore"
import { getUseUserLocationShareMapNodes } from "features/map/map-nodes/useUserLocationShareMapNodes"
import { togglePlayback } from "plugins/shared/playback/state/slice"
import { getGenericLayerDataInspectorPanelComponent } from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import { InspectorPanelUserLocationShareLayer } from "features/map/inspector-panel/user-location-shares/InspectorPanelUserLocationShareLayer"
import { InspectorPanelUserLocationShareData } from "features/map/inspector-panel/user-location-shares/InspectorPanelUserLocationShareData"
import { getGenericAssetsListItem } from "panels/content/side-panel/generics/genericAssetsListItems"
import { UserLocationShareAssetsThumbnail } from "panels/content/side-panel/assets-thumbnails/UserLocationShareAssetsThumbnail"
import {
    getGenericLayerIconComponent,
    getGenericLayerListItem,
} from "panels/content/side-panel/generics/GenericLayerListItem"
import { zoomToShowPoints } from "features/map/zoom/thunks"
import store from "integrations/redux/store"
import { PluginRegistry, PluginSpec } from "plugins/plugin-registry"
import { selectIsUserLocationShareOpen } from "./state/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { UserLocationFlyoutLayersPanelTrigger } from "./ui/UserLocationFlyoutLayersPanelTrigger"
import { UserLocationFlyoutLayersPanelMenu } from "./ui/UserLocationFlyoutLayersPanelMenu"
import {
    getGenericFlyoutLayerListItem,
    getUseVisibilityActions,
} from "./ui/UserLocationFlyoutListItem"

const useUserLocationShareMapLayers = () => [
    ...useUserLocationPathLayer(),
    ...useUserLocationLayers(),
    ...useUserLocationSharePlaybackLayers(),
]

const zoomToUserLocationShare = (entity: UserLocationShare) => {
    if (!entity.initialCoordinate) return
    store.dispatch(
        zoomToShowPoints({
            points: [[entity.initialCoordinate.longitude, entity.initialCoordinate.latitude]],
        })
    )
}

export function registerUserLocationSharePlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "userLocationShares",
            displayName: "Broadcast",
            sidePanelLayerType: SidePanelLayerType.UserLocationShare,
            PrimaryIcon: Icons.LocationOn,
            ImageIconUrl: "/images/plugins/layers/broadcast.svg",
            updateAction: updateUserLocationShare,
            deleteAction: deleteUserLocationShare,
            confirmDelete: () => true,
            protobufMessage: UserLocationShare,
            selectAllEntities: selectUserLocationShares,
            selectSingleEntity: selectUserLocationShareById,
            zoomTo: zoomToUserLocationShare,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            useSidePanelEntities: getUseSidePanelEntities({
                protobufMessage: spec.protobufMessage,
                selectSingleEntity: spec.selectSingleEntity,
                selectAllEntities: selectNonLiveUserLocationShares,
            }),
            SidePanelListItem: getGenericLayerListItem({
                spec,
                LayerIconComponent: getUserLocationShareLayerIconComponent(spec),
            }),
        }))
        .implement("top-menu-flyout-layers-plugin", (spec) => ({
            flyoutId: "atlas",
            menuConfig: {
                TriggerComponent: UserLocationFlyoutLayersPanelTrigger,
                MenuComponent: UserLocationFlyoutLayersPanelMenu,
            },
            useEntities: getUseSidePanelEntities({
                protobufMessage: spec.protobufMessage,
                selectSingleEntity: spec.selectSingleEntity,
                selectAllEntities: selectLiveUserLocationShares,
            }),
            LayerListItem: getGenericFlyoutLayerListItem({
                showLiveBadge: false,
                useActions: getUseVisibilityActions(spec),
                spec,
            }),
        }))
        .implement("side-panel-assets-plugin", (spec) => ({
            useSidePanelEntities: getUseSidePanelEntities({
                protobufMessage: spec.protobufMessage,
                selectSingleEntity: spec.selectSingleEntity,
                selectAllEntities: selectNonLiveUserLocationShares,
            }),
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: UserLocationShareAssetsThumbnail,
                onClick: ({ id }) => {
                    store.dispatch(togglePlayback({ id, type: "locationShare" }))
                },
            }),
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useUserLocationShareMapLayers,
        }))
        .implement("map-node-plugin", (spec) => ({
            useMapNodes: getUseUserLocationShareMapNodes(spec),
        }))
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getGenericLayerDataInspectorPanelComponent({
                spec,
                LayerComponent: InspectorPanelUserLocationShareLayer,
                DataComponent: InspectorPanelUserLocationShareData,
            }),
        }))
}

function getUserLocationShareLayerIconComponent(spec: PluginSpec<UserLocationShare>) {
    const GenericLayerIconComponent = getGenericLayerIconComponent(spec)
    const UserLocationShareLayerIconComponent = ({ id }: { id: string }) => {
        const isActive = useAppSelector(selectIsUserLocationShareOpen(id))
        const isPlaying = userLocationSharePlaybackStore.useItem(id)?.isPlaying ?? false

        if (isActive) {
            return (
                <div className="w-5 h-5 flex justify-center items-center">
                    <Icons.ExtraSmallIcon
                        className="shrink-0"
                        Icon={isPlaying ? Icons.PlayCircle : Icons.PauseCircle}
                        iconClassName="text-blue-500"
                    />
                </div>
            )
        }
        return <GenericLayerIconComponent id={id} />
    }
    return UserLocationShareLayerIconComponent
}
